import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { serializeParams } from "@/utils/apiUtils";
import { URLQuery } from "@/utils/queryUtils";
import { openUrl } from "@/utils/utils";
import { MenuInfo } from "@rc-component/menu/es/interface";
import { App as AntApp, Button, Dropdown, Space } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router";
import t from "../../../../../app/i18n";
import { useDeleteContract, useEnumeration, useGetContractExternalView } from "../../../../../common/api/queries";
import { QueryKeys } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { hasPermission } from "../../../../../common/security/utils";
import { useHasPermissions, usePermissions } from "../../../../auth/hooks";
import { CALC_ROUTE_PATHS } from "../../../../calculator/paths";
import { CONTRACT_FORMS_TABS } from "../../../../contractform/utils";
import { EnumerationType } from "../../../../enumerations/enums";
import { ContractVerificationStatus, InsuranceType } from "../../../enums";
import { CONTRACT_ROUTE_PATHS } from "../../../paths";
import { Contract, Insurance } from "../../../types";
import {
  contractPermissionsByContractType,
  isInsuranceContract,
  isOtherVehicleInsurance,
  isTravelOrRealtyOrVehicleInsurance,
  isVehicleInsurance,
} from "../../../utils";
import { ContractVerificationStatusButtonView } from "./ContractVerificationStatusButtonView";

const TERMINATION_ACTION_KEY = "contract-dropdown-termination";
const REGISTER_VEHICLE_DATA_ACTION_KEY = "contract-dropdown-register-vehicle-data";
const DELETE_ACTION_KEY = "contract-dropdown-delete";
const LOAD_TO_CALCULATOR_ACTION_KEY = "contract-dropdown-load-calculator";

const getDropdownItems = (contract: Contract, permissions: Permission[]) => {
  const hasDeletePermission = hasPermission(permissions, contractPermissionsByContractType[contract.type].delete);
  const dropdownItems: ItemType[] = [];

  if (isInsuranceContract(contract)) {
    dropdownItems.push({
      key: TERMINATION_ACTION_KEY,
      label: (
        <Link
          to={
            generatePath(CONTRACT_ROUTE_PATHS.forms.to) +
            "?" +
            serializeParams({
              tab: CONTRACT_FORMS_TABS.TERMINATION,
              [QueryKeys.CONTRACT_NUMBER]: contract.identifier,
            })
          }
          target="_blank"
        >
          <ActionTextIcon text={t("contract.actions.terminate")} color="orange" icon="stop" />
        </Link>
      ),
    });

    const insurance = contract.insurances[0] as Insurance;

    // The API updates the license plate of a VEHICLE insurance as well, even though it has no vehicle data
    if (isVehicleInsurance(insurance) || isOtherVehicleInsurance(insurance)) {
      dropdownItems.push({
        key: REGISTER_VEHICLE_DATA_ACTION_KEY,
        label: (
          <Link
            to={`${CONTRACT_ROUTE_PATHS.forms.to}${URLQuery.searchParamsToString({
              searchParams: {
                [QueryKeys.TAB]: CONTRACT_FORMS_TABS.REGISTER_VEHICLE_DATA,
                [QueryKeys.CONTRACT_NUMBER]: contract.identifier,
              },
            })}`}
          >
            <ActionTextIcon text={t("contract.actions.registerVehicleData")} color="blue" icon="car" />
          </Link>
        ),
      });
    }

    let hasLoadToCalculatorPermission = false;
    let calcPath = "";

    switch (insurance.type) {
      case InsuranceType.MTPL:
        calcPath = CALC_ROUTE_PATHS.vehicle.to;
        hasLoadToCalculatorPermission = hasPermission(permissions, Permission.MTPL_CALCULATE);
        break;
      case InsuranceType.CRASH:
      case InsuranceType.GAP:
      case InsuranceType.PAS:
        calcPath = CALC_ROUTE_PATHS.vehicle.to;
        hasLoadToCalculatorPermission = hasPermission(permissions, Permission.CRASH_CALCULATE);
        break;
      case InsuranceType.REALTY:
        calcPath = CALC_ROUTE_PATHS.realty.to;
        hasLoadToCalculatorPermission = hasPermission(permissions, Permission.REALTY_CALCULATE);
        break;
      case InsuranceType.TRAVEL:
        calcPath = CALC_ROUTE_PATHS.travel.to;
        hasLoadToCalculatorPermission = hasPermission(permissions, Permission.TRAVEL_CALCULATE);
        break;
    }

    if (hasLoadToCalculatorPermission && isTravelOrRealtyOrVehicleInsurance(insurance)) {
      dropdownItems.push({
        key: LOAD_TO_CALCULATOR_ACTION_KEY,
        label: (
          <ActionTextIcon
            path={`${calcPath}${URLQuery.searchParamsToString({ searchParams: { [QueryKeys.CONTRACT_ID]: contract.id } })}`}
            target="_blank"
            icon="select"
            color="green"
            text={t("contract.actions.loadToCalculator")}
          />
        ),
      });
    }
  }

  if (hasDeletePermission) {
    dropdownItems.push({
      key: DELETE_ACTION_KEY,
      label: <ActionTextIcon text={t("contract.actions.deleteContract")} color="red" icon="delete" />,
    });
  }

  return dropdownItems;
};

interface Props {
  contract: Contract;
  onUpdate: () => void;
}

export const ContractHeaderActionsView = ({ contract, onUpdate }: Props) => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const navigate = useNavigate();

  const permissions = usePermissions();
  const contractExternalViewInstitutionIds = useEnumeration(EnumerationType.CONTRACT_EXTERNAL_VIEW_INSTITUTIONS);

  const hasVerificationStatusChangePermission = useHasPermissions(
    contractPermissionsByContractType[contract.type].verificationStatusChange,
  );

  const hasUpdatePermission = useHasPermissions(contractPermissionsByContractType[contract.type].update);
  const hasLimitedUpdatePermission = useHasPermissions(contractPermissionsByContractType[contract.type].limitedUpdate);

  const getContractExternalView = useGetContractExternalView();
  const deleteContract = useDeleteContract();

  const dropdownItems = getDropdownItems(contract, permissions);

  const handleMenuDropdownClick = (clickInfo: MenuInfo) => {
    switch (clickInfo.key) {
      case DELETE_ACTION_KEY:
        modal.confirm({
          title: t("contract.helpers.deleteContractConfirm"),
          okText: t("common.yes"),
          cancelText: t("common.no"),
          onOk: () => {
            deleteContract.mutate(contract.id, {
              onSuccess: () => navigate(CONTRACT_ROUTE_PATHS.list.to, { replace: true }),
            });
          },
        });
        break;
    }
  };

  return (
    <Space>
      {(hasUpdatePermission ||
        (contract.verificationStatus === ContractVerificationStatus.TYPED && hasLimitedUpdatePermission)) && (
        <Button type="primary" icon={<AntIcon type="edit" />} onClick={onUpdate}>
          {t("contract.actions.updateContract")}
        </Button>
      )}

      <ContractVerificationStatusButtonView
        contract={contract}
        hasChangePermission={hasVerificationStatusChangePermission}
      />

      {contractExternalViewInstitutionIds.includes(contract.institution.id) && (
        <Button
          icon={<AntIcon type="delivered-procedure" />}
          loading={getContractExternalView.isPending}
          onClick={() =>
            getContractExternalView.mutate(contract.id, {
              onSuccess: ({ redirectUrl }) => openUrl(redirectUrl, "_blank"),
            })
          }
        >
          {t("contract.actions.showInExternalView")}
        </Button>
      )}

      {dropdownItems.length > 0 && (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: dropdownItems,
            onClick: handleMenuDropdownClick,
          }}
        >
          <Button icon={<AntIcon type="more" />} />
        </Dropdown>
      )}
    </Space>
  );
};
